/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setFixedNavbar,
  setDarkMode,
  setSidenavMode,
} from "context";

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();
  const { openConfigurator, fixedNavbar, darkMode, sidenavMode } = controller;
  const theme = useTheme();
  const isDocked = useMediaQuery(theme.breakpoints.up("navOverlay"));

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(dispatch, next);
    localStorage.setItem("darkMode", next ? "true" : "false");
  };

  // `sidenavHidden` is derived from mode + breakpoint by the single effect in App.js — writing it
  // here too would fight that effect (and would wrongly unmount the drawer below xl).
  const handleModeChange = (mode) => {
    setSidenavMode(dispatch, mode);
    localStorage.setItem("sidenavMode", mode);
  };

  const handleReset = () => {
    setSidenavMode(dispatch, "auto");
    setFixedNavbar(dispatch, false);
    setDarkMode(dispatch, false);
    localStorage.setItem("sidenavMode", "auto");
    localStorage.setItem("darkMode", "false");
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1.5}
        pt={3}
        pb={2}
        px={3}
        sx={{ minWidth: 0 }}
      >
        <MDBox minWidth={0}>
          <MDTypography variant="h5">Özelleştir</MDTypography>
          <MDTypography variant="body2" color="text" sx={{ overflowWrap: "anywhere" }}>
            Navigasyon ve görünüm ayarları
          </MDTypography>
        </MDBox>

        <IconButton
          aria-label="Özelleştirme panelini kapat"
          onClick={handleCloseConfigurator}
          sx={{ flex: "0 0 auto" }}
        >
          <Icon>close</Icon>
        </IconButton>
      </MDBox>

      <Divider />

      <MDBox py={2.5} px={3} width="100%" sx={{ minWidth: 0, overflowX: "hidden" }}>
        <MDBox lineHeight={1}>
          <MDTypography variant="h6">Navigasyon</MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ overflowWrap: "anywhere" }}
          >
            Menünün farklı ekran genişliklerinde nasıl davranacağını seçin.
          </MDTypography>
          {!isDocked && (
            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
              Bu ekran genişliğinde menü her zaman üst çubuktaki düğmeden açılır.
            </MDTypography>
          )}
          <MDBox mt={2} display="grid" gap={1} sx={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
            {[
              {
                value: "auto",
                icon: "devices",
                label: "Otomatik",
                description: "Geniş ekranda tam, darda simge",
              },
              {
                value: "pinned",
                icon: "push_pin",
                label: "Sabit",
                description: "Geniş ekranda her zaman tam",
              },
              {
                value: "hidden",
                icon: "visibility_off",
                label: "Gizli",
                description: "Sol kenar şeridinden açılır",
              },
            ].map(({ value, icon, label, description }) => {
              const selected = sidenavMode === value;
              return (
                <MDBox
                  key={value}
                  component="button"
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleModeChange(value)}
                  sx={(theme) => ({
                    appearance: "none",
                    width: "100%",
                    minWidth: 0,
                    minHeight: 70,
                    p: 1.5,
                    font: "inherit",
                    textAlign: "left",
                    color: selected ? theme.palette.accent.onSurface : theme.palette.text.main,
                    backgroundColor: selected
                      ? theme.palette.accent.soft
                      : theme.palette.background.paper,
                    border: `1px solid ${
                      selected ? theme.palette.accent.main : theme.palette.borderColor.main
                    }`,
                    borderRadius: theme.borders.borderRadius.lg,
                    cursor: "pointer",
                    transition: theme.transitions.create(["background-color", "border-color"]),
                    "&:hover": {
                      backgroundColor: selected
                        ? theme.palette.accent.soft
                        : theme.palette.background.default,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${theme.palette.accent.main}`,
                      outlineOffset: 2,
                    },
                  })}
                >
                  <MDBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                    <Icon sx={{ flex: "0 0 auto", fontSize: "1.25rem !important" }}>{icon}</Icon>
                    <MDBox flex={1} minWidth={0}>
                      <MDTypography variant="button" fontWeight="medium" display="block">
                        {label}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        mt={0.25}
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {description}
                      </MDTypography>
                    </MDBox>
                    {selected && (
                      <Icon sx={{ flex: "0 0 auto", fontSize: "1rem !important" }}>
                        check_circle
                      </Icon>
                    )}
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        </MDBox>

        <MDBox
          mt={2}
          py={1.5}
          display="grid"
          sx={{
            gridTemplateColumns: "auto minmax(0, 1fr) auto",
            columnGap: 1.25,
            rowGap: 0.25,
            alignItems: "center",
          }}
        >
          <Icon color="info">vertical_align_top</Icon>
          <MDTypography variant="button" fontWeight="medium" minWidth={0}>
            Üst çubuğu sabitle
          </MDTypography>
          <Switch
            checked={fixedNavbar}
            onChange={handleFixedNavbar}
            color="info"
            inputProps={{ "aria-label": "Üst çubuğu sabitle" }}
            sx={{ gridColumn: 3, gridRow: 1 }}
          />
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ gridColumn: "2 / -1", overflowWrap: "anywhere" }}
          >
            Sayfa kayarken birim ve hesap kontrolleri görünür kalsın.
          </MDTypography>
        </MDBox>

        <Divider />

        <MDBox mt={2.5} lineHeight={1}>
          <MDTypography variant="h6">Görünüm</MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ overflowWrap: "anywhere" }}
          >
            Çalışma ortamınıza uygun kontrastı seçin.
          </MDTypography>
          <MDBox
            mt={1.5}
            py={1.5}
            display="grid"
            sx={{
              gridTemplateColumns: "auto minmax(0, 1fr) auto",
              columnGap: 1.25,
              rowGap: 0.25,
              alignItems: "center",
            }}
          >
            <Icon color="info">{darkMode ? "dark_mode" : "light_mode"}</Icon>
            <MDTypography variant="button" fontWeight="medium" minWidth={0}>
              Karanlık mod
            </MDTypography>
            <Switch
              checked={darkMode}
              onChange={handleDarkMode}
              color="info"
              inputProps={{ "aria-label": "Karanlık modu aç veya kapat" }}
              sx={{ gridColumn: 3, gridRow: 1 }}
            />
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              sx={{ gridColumn: "2 / -1", overflowWrap: "anywhere" }}
            >
              {darkMode ? "Koyu görünüm etkin" : "Açık görünüm etkin"}
            </MDTypography>
          </MDBox>
        </MDBox>

        <Divider />

        <MDButton
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleReset}
          startIcon={<Icon>restart_alt</Icon>}
          sx={{
            mt: 2.5,
            minWidth: 0,
            whiteSpace: "normal",
            "& .MuiButton-startIcon": { flex: "0 0 auto" },
          }}
        >
          Varsayılan görünümü geri yükle
        </MDButton>
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
